/**
 * QuickLogBar — one-tap channel logging for the BDR landing page.
 *
 * Nine buttons matching the activity-audit spec channel set
 * (Phone Dial, Phone Connect, Email Send, Email Reply, LinkedIn Msg,
 * LinkedIn Accept, SMS, In-Person, Meeting Set). Each tap:
 *   1. Increments the rep-local channel counter (bdr-channel-store).
 *   2. Fires `logActivity()` to insert an activity_log row in Supabase
 *      with the channel + action metadata. Account/contact are NULL —
 *      this is a quick-tally surface, not a contact-attributed log.
 *   3. Notifies the parent via `onChange(counts)` so KPI tiles update.
 *
 * The Supabase write is fire-and-forget; the UI never blocks on it.
 * If the activity_log migration has not been applied, the write is a
 * no-op (see dial-log-client.ts MIGRATION_APPLIED guard).
 */
import { useState, useCallback } from "react";
import { tokens } from "@/components/operator/tokens";
import { logActivity } from "@/components/dial-and-log/dial-log-client";
import { getSession, getCurrentTenant } from "@/lib/session";
import { useToast } from "@/hooks/use-toast";
import {
  CHANNEL_KEYS,
  CHANNEL_LABELS,
  CHANNEL_COLOR,
  incrementChannel,
  getChannelCounts,
  type BdrChannel,
  type ChannelCounts,
} from "@/lib/bdr-channel-store";
import type { ActivityChannel, ActivityAction } from "@/components/dial-and-log/types";

// Map QuickLog channels to the activity_log table channel+action enum.
// SMS, in_person, meeting_set are NOT in the current migration; those
// writes will be dropped by the CHECK constraint until the schema is
// extended. See bdr-channel-split-report.md → migration spec.
const TABLE_MAPPING: Record<
  BdrChannel,
  { channel: ActivityChannel | "sms" | "in_person" | "meeting"; action: ActivityAction | "meeting_set" }
> = {
  phone_dial:       { channel: "phone",            action: "dial_attempted" },
  phone_connect:    { channel: "phone",            action: "connected" },
  email_send:       { channel: "email",            action: "sent" },
  email_reply:      { channel: "email",            action: "connected" },
  linkedin_msg:     { channel: "linkedin_dm",      action: "sent" },
  linkedin_connect: { channel: "linkedin_connect", action: "connected" },
  sms:              { channel: "sms",              action: "sent" },
  in_person:        { channel: "in_person",        action: "connected" },
  meeting_set:      { channel: "meeting",          action: "meeting_set" },
};

interface QuickLogBarProps {
  onChange?: (counts: ChannelCounts) => void;
}

export default function QuickLogBar({ onChange }: QuickLogBarProps) {
  const session = getSession();
  const tenant = getCurrentTenant();
  const userId = session?.id ?? "current_user";
  const { toast } = useToast();
  const [pulse, setPulse] = useState<BdrChannel | null>(null);
  const [counts, setCounts] = useState<ChannelCounts>(() =>
    getChannelCounts(userId),
  );

  const handleTap = useCallback(
    (channel: BdrChannel) => {
      const updated = incrementChannel(userId, channel);
      setCounts(updated);
      onChange?.(updated);

      // Brief visual pulse.
      setPulse(channel);
      window.setTimeout(() => setPulse((p) => (p === channel ? null : p)), 250);

      // Fire-and-forget Supabase insert. NULL account/contact is correct for
      // the quick-log surface (global tile, no account in context);
      // per-contact attribution comes from dial buttons inside the contact card.
      const mapping = TABLE_MAPPING[channel];
      const tenantId = tenant?.id ?? 0;
      if (session?.id && tenantId > 0) {
        logActivity({
          tenant_id: tenantId,
          account_id: null,  // Bug A fix: was 0 (FK violation); NULL = global activity
          contact_id: null,
          user_id: session.id,
          channel: mapping.channel as ActivityChannel,
          action: mapping.action as ActivityAction,
          outcome_notes: null,
          attempt_number: 1,
        }).catch((err: unknown) => {
          // Bug A fix: surface real failures instead of swallowing them
          const msg = err instanceof Error ? err.message : String(err);
          console.error("[QuickLogBar] activity_log write failed:", msg);
          toast({
            title: "Activity log error",
            description: "Could not record this tap. Your count was saved locally.",
            variant: "destructive",
          });
        });
      }
    },
    [userId, onChange, session?.id, tenant?.id],
  );

  return (
    <div
      style={{
        background: tokens.color.canvas,
        border: `1px solid ${tokens.color.border}`,
        borderRadius: tokens.radius.card,
        padding: 16,
        marginBottom: 20,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          marginBottom: 12,
        }}
      >
        <div
          style={{
            fontFamily: tokens.font.display,
            fontSize: 14,
            fontWeight: 700,
            color: tokens.color.textPrimary,
          }}
        >
          Quick Log
        </div>
        <div
          style={{
            fontSize: 12,
            color: tokens.color.textMuted,
            fontFamily: tokens.font.body,
          }}
        >
          One tap per touch. Counts roll up to today's tiles.
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
          gap: 8,
        }}
      >
        {CHANNEL_KEYS.map((ch) => {
          const accent = CHANNEL_COLOR[ch];
          const n = counts[ch] || 0;
          const isPulsing = pulse === ch;
          return (
            <button
              key={ch}
              type="button"
              onClick={() => handleTap(ch)}
              aria-label={`Log ${CHANNEL_LABELS[ch]}`}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 4,
                padding: "10px 12px",
                background: isPulsing ? accent : tokens.color.surfaceMute,
                color: isPulsing ? "#FFFFFF" : tokens.color.textPrimary,
                border: `1px solid ${isPulsing ? accent : tokens.color.border}`,
                borderRadius: 6,
                cursor: "pointer",
                fontFamily: tokens.font.body,
                fontSize: 12,
                fontWeight: 600,
                textAlign: "left",
                transition: "background 120ms ease, color 120ms ease, border-color 120ms ease",
                minHeight: 56,
              }}
            >
              <span
                style={{
                  display: "inline-block",
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: accent,
                }}
                aria-hidden
              />
              <span>{CHANNEL_LABELS[ch]}</span>
              <span
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  fontFamily: tokens.font.display,
                  color: isPulsing ? "#FFFFFF" : tokens.color.textPrimary,
                }}
              >
                {n}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
